import React, { useState } from 'react';
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { Delete, Edit } from '@mui/icons-material';
import FuseLoading from '@fuse/core/FuseLoading';
import SimpleDialog from '@fuse/core/SimpleDialog';
import {
  useGetAllTemplatesCategoryQuery,
  useDeleteTemplatesCategoryMutation,
} from 'src/app/services/templatesCategoryApi';
import TemplateCategoriesForm from './TemplateCategoriesForm';
import useMessage from 'src/app/hooks/useMessage';
import handleError from '@fuse/core/errorHandler';

const TemplateCategories = () => {
  const { t, i18n } = useTranslation('translations');
  const lang = i18n.language;
  const [currentCategoryId, setCurrentCategoryId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { data: templateCategories = [], refetch, isLoading } = useGetAllTemplatesCategoryQuery();
  const [deleteCategory] = useDeleteTemplatesCategoryMutation();
  const { showMessage } = useMessage();

  const showAddDialog = () => {
    setCurrentCategoryId(null);
    setIsDialogOpen(true);
  };

  const showEditDialog = (id) => {
    setCurrentCategoryId(id);
    setIsDialogOpen(true);
  };

  const handleDelete = (id) => {
    deleteCategory(id)
      .unwrap()
      .then(() => {
        showMessage({ message: t('TemplateCategorySuccessfullyDeleted'), variant: 'success' });
        refetch();
      })
      .catch(handleError);
    refetch();
  };

  return (
    <div style={{ display: 'grid' }}>
      {isLoading ? (
        <FuseLoading />
      ) : (
        <TableContainer>
          <div className="h-[40px] flex flex-row justify-between items-center dark:bg-dark">
            <h2 className="ml-5">{t('TemplateCategories')}</h2>
            <Button
              className="mx-8"
              size="small"
              variant="outlined"
              color="secondary"
              onClick={showAddDialog}
            >
              <FuseSvgIcon size={20}>react-outline:plus</FuseSvgIcon>
              <span className="mx-8">{t('Add')}</span>
            </Button>
          </div>
          <Table style={{ overflow: 'auto' }}>
            <TableBody className="overflow-y-auto">
              {templateCategories.map((item) => (
                <TableRow key={item.id} className="bg-white hover:bg-grey-200 dark:bg-dark">
                  <TableCell>{lang === 'uz' ? item.nameUz : item.nameRu}</TableCell>
                  <TableCell>
                    <div className="flex justify-end">
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        className="mr-5"
                        color="info"
                        onClick={() => showEditDialog(item.id)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        className="mr-5"
                        color="error"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Delete />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <SimpleDialog
        title={currentCategoryId ? t('EditTemplateCategory') : t('AddTemplateCategory')}
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      >
        <TemplateCategoriesForm
          id={currentCategoryId}
          refetch={refetch}
          onClose={() => setIsDialogOpen(false)}
          isEdit={!!currentCategoryId}
        />
      </SimpleDialog>
    </div>
  );
};

export default TemplateCategories;
